export type ConsentStatus = "accepted_all" | "rejected_all" | "custom";

export interface ConsentPreferences {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentState {
  status: ConsentStatus;
  preferences: ConsentPreferences;
  updatedAt: string;
}

export const CONSENT_CHANGE_EVENT = "thinkeat:consent-changed";

const CONSENT_STORAGE_KEY = "thinkeat_cookie_consent";

const VALID_STATUSES: ConsentStatus[] = ["accepted_all", "rejected_all", "custom"];

const DEFAULT_PREFERENCES: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

function isBrowserEnvironment(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizePreferences(value: unknown): ConsentPreferences {
  const candidate = (value as Partial<ConsentPreferences> | null) ?? {};

  return {
    necessary: true,
    analytics: Boolean(candidate.analytics),
    marketing: Boolean(candidate.marketing),
  };
}

function isValidStatus(status: unknown): status is ConsentStatus {
  return typeof status === "string" && VALID_STATUSES.includes(status as ConsentStatus);
}

function clearInvalidConsent(): void {
  if (!isBrowserEnvironment()) return;
  window.localStorage.removeItem(CONSENT_STORAGE_KEY);
}

export function getConsent(): ConsentState | null {
  if (!isBrowserEnvironment()) return null;

  const rawValue = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  if (!rawValue) return null;

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<ConsentState> | null;

    if (!parsedValue || !isValidStatus(parsedValue.status)) {
      clearInvalidConsent();
      return null;
    }

    return {
      status: parsedValue.status,
      preferences: normalizePreferences(parsedValue.preferences),
      updatedAt: typeof parsedValue.updatedAt === "string" ? parsedValue.updatedAt : new Date().toISOString(),
    };
  } catch {
    clearInvalidConsent();
    return null;
  }
}

export function setConsent(input: Pick<ConsentState, "status" | "preferences">): ConsentState {
  const consent: ConsentState = {
    status: input.status,
    preferences: normalizePreferences(input.preferences),
    updatedAt: new Date().toISOString(),
  };

  if (isBrowserEnvironment()) {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    window.dispatchEvent(
      new CustomEvent(CONSENT_CHANGE_EVENT, {
        detail: consent,
      }),
    );
  }

  return consent;
}

export function canLoadAnalytics(): boolean {
  return getConsent()?.preferences.analytics === true;
}

export function canLoadMarketing(): boolean {
  return getConsent()?.preferences.marketing === true;
}

export function hasConsentDecision(): boolean {
  return getConsent() !== null;
}

export function getDefaultConsentPreferences(): ConsentPreferences {
  return { ...DEFAULT_PREFERENCES };
}
